import React from 'react'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { mobile } from './tokens'

interface MobileExpandableProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  badges?: React.ReactNode
  open: boolean
  onToggle: () => void
  children: React.ReactNode
  actions?: React.ReactNode
  className?: string
  /** Whole-card navigation (chevron + actions stop propagation). */
  onCardClick?: () => void
}

export function MobileExpandable({
  title,
  subtitle,
  badges,
  open,
  onToggle,
  children,
  actions,
  className = '',
  onCardClick,
}: MobileExpandableProps) {
  return (
    <div className={`${mobile.surface} overflow-hidden ${className}`.trim()}>
      <div
        className={`p-4 ${onCardClick ? 'cursor-pointer active:bg-gray-50/80' : ''}`.trim()}
        onClick={onCardClick}
        onKeyDown={
          onCardClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onCardClick()
                }
              }
            : undefined
        }
        role={onCardClick ? 'link' : undefined}
        tabIndex={onCardClick ? 0 : undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {badges && (
              <div className="flex items-center gap-2 flex-wrap mb-1.5 w-full">{badges}</div>
            )}
            <div className="text-sm font-semibold text-gray-900">{title}</div>
            {subtitle && <div className="mt-1 text-xs text-gray-600 space-y-0.5">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggle()
            }}
            className={`${mobile.ghostBtn} flex-shrink-0`}
            aria-expanded={open}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
          </button>
        </div>
        {actions && (
          <div
            className="flex flex-wrap items-center gap-2 mt-3"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
      {open && (
        <div className="border-t border-gray-100 bg-gray-50/80 p-4 space-y-4">{children}</div>
      )}
    </div>
  )
}
